import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, MapPin, Rocket, Sparkle } from "lucide-react";
import { Chapter, MaskLine, Reveal } from "@/components/motion";
import { txt } from "@/lib/site";

const card = "bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 hover:-translate-y-1 transition duration-300";

export function Marquee() {
  const row = ["Selamatkan 1 Jiwa Lagi", "GIM Church Makassar", "One More Soul", "Bertumbuh Bersama"];
  const Item = () => (
    <div className="flex items-center gap-10 pr-10">
      {row.map((t, i) => (
        <span key={t} className="flex items-center gap-10">
          <span className={i % 2 ? "text-outline" : "text-white"}>{t}</span>
          <Sparkle className={`w-10 h-10 ${i % 2 ? "text-indigo-500" : "text-blue-500"}`} />
        </span>
      ))}
    </div>
  );
  return (
    <section aria-hidden="true" className="bg-slate-900 py-14 md:py-20 overflow-hidden border-y border-slate-800">
      <div className="marquee-track flex w-max whitespace-nowrap font-black tracking-tighter text-6xl md:text-8xl"><Item /><Item /></div>
    </section>
  );
}

const TIMELINE = [
  ["1999", "Pelayanan dimulai dari ruko lantai 1 oleh Alm. Pdt. Jhonsy Gomulya & Pdt. Elly Lay."],
  ["2018", "Penggembalaan dilanjutkan oleh Pdt. Elly Lay."],
  ["2026", "Pdt. Hendra David S.T., M.Pd. ditahbiskan sebagai Gembala Sidang."],
];

export function About({ site }) {
  const s = site || {};
  const [h1, ...rest] = (s.about_heading || "Dari sebuah ruko, untuk satu jiwa lagi.").split(",");
  const milestones = (s.milestones && s.milestones.length)
    ? s.milestones.map((m) => [m.year, m.text])
    : TIMELINE;
  return (
    <section id="tentang" data-testid="about-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
      <Reveal><Chapter n="✦" label={txt(site, "about_label", "Tentang GIM Church")} /></Reveal>
      <div className="mt-10 max-w-4xl">
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[0.95]">
          <MaskLine inView>{h1}{rest.length ? "," : ""}</MaskLine>
          {rest.length > 0 && <MaskLine inView delay={0.1} className="text-slate-300">{rest.join(",").trim()}</MaskLine>}
        </h2>
        <Reveal delay={0.1}>
          <p className="mt-8 text-slate-500 leading-relaxed max-w-2xl whitespace-pre-line" data-testid="about-text">{s.about_text}</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          <Reveal className={card} delay={0.1}>
            <span className="w-11 h-11 rounded-2xl bg-blue-100 text-blue-700 grid place-items-center"><Eye className="w-5 h-5" /></span>
            <p className="mt-5 text-xs font-bold uppercase tracking-widest text-slate-500">Visi</p>
            <p className="mt-2 text-3xl font-black tracking-tight text-slate-900" data-testid="about-vision">{s.vision}</p>
          </Reveal>
          <Reveal className={card} delay={0.2}>
            <span className="w-11 h-11 rounded-2xl bg-indigo-100 text-indigo-600 grid place-items-center"><Rocket className="w-5 h-5" /></span>
            <p className="mt-5 text-xs font-bold uppercase tracking-widest text-slate-500">Misi</p>
            <p className="mt-2 font-semibold text-slate-800 leading-relaxed" data-testid="about-mission">{s.mission}</p>
          </Reveal>
        </div>
        <div className="mt-12 space-y-0" data-testid="about-timeline">
          {milestones.map(([y, t], i) => (
            <Reveal key={(y || "") + i} delay={i * 0.08} className="grid grid-cols-[5rem_1fr] gap-6 py-5 border-t border-slate-200">
              <span className="text-2xl font-black text-blue-600 tabular-nums">{y}</span>
              <span className="text-slate-600">{t}</span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const DIGITS = [["26", "Tahun", "2026"], ["09", "Bulan", "September"], ["1", "Gender", "1 Pria · 2 Wanita"], ["001", "No. Urut", "Pendaftaran"]];

export function NigInfo({ site }) {
  return (
    <section id="nig" data-testid="nig-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
      <Reveal><Chapter n="02" label={txt(site, "nig_label", "Sistem Identitas GIM")} /></Reveal>
      <div className="mt-10 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-5">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[0.95]"><MaskLine inView>{txt(site, "nig_heading", "Apa itu NIG?")}</MaskLine></h2>
          <Reveal delay={0.1}><p className="mt-6 text-slate-500 leading-relaxed" data-testid="nig-text">{txt(site, "nig_text", "NIG (Nomor Induk Gereja) adalah identitas digital unik jemaat GIM Church.")}</p></Reveal>
          <Reveal delay={0.2}>
            <Link to="/register" data-testid="nig-cta-button" className="mt-8 inline-flex items-center gap-2 bg-blue-600 text-white font-bold py-3 md:py-4 px-7 rounded-full shadow-md hover:bg-blue-700 transform active:scale-95 transition">Dapatkan NIG Saya Sekarang <ArrowUpRight className="w-4 h-4" /></Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7 grid grid-cols-4 gap-3 md:gap-4" data-testid="nig-digits">
          {DIGITS.map(([d, label, hint], i) => (
            <Reveal key={label} delay={i * 0.1} className={`rounded-3xl p-4 md:p-6 border ${i === 3 ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-100 shadow-sm"}`}>
              <p className="text-3xl md:text-6xl font-black tracking-tighter tabular-nums">{d}</p>
              <p className={`mt-4 text-xs font-bold uppercase tracking-widest ${i === 3 ? "text-blue-100" : "text-slate-900"}`}>{label}</p>
              <p className={`text-xs mt-1 ${i === 3 ? "text-blue-100" : "text-slate-500"}`}>{hint}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const CAT = { Umum: "bg-blue-100 text-blue-700", Youth: "bg-indigo-100 text-indigo-700", Kids: "bg-amber-100 text-amber-700" };

export function Schedule({ items = [], site }) {
  return (
    <section id="jadwal" data-testid="schedule-section" className="bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
        <Reveal><Chapter n="03" label={txt(site, "schedule_label", "Jadwal Ibadah Mingguan")} /></Reveal>
        <h2 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[0.95]"><MaskLine inView>{txt(site, "schedule_heading", "Temukan ibadahmu.")}</MaskLine></h2>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className="bg-slate-50 rounded-3xl p-6 md:p-8 border border-slate-100 hover:-translate-y-1 hover:shadow-md transition duration-300" data-testid={`schedule-card-${i}`}>
              <span className={`text-[11px] font-bold rounded-full px-3 py-1 ${CAT[s.category] || "bg-slate-100 text-slate-700"}`}>{s.category}</span>
              <p className="mt-6 text-5xl font-black tracking-tighter text-slate-900 tabular-nums">{s.time}</p>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">WITA</p>
              <p className="mt-6 font-extrabold text-slate-900">{s.title}</p>
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{s.day}, {s.location}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
